/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  BEResponseVoid,
  GetBackListeElettoraliIdMembriParams,
  GetBackListeElettoraliParams,
  ListaElettorale,
  ListaElettoraleRequest,
  PostBackListeElettoraliIdImportBody,
  PutBackListeElettoraliIdImmagineBody,
  WsRispostaPaginataListListaElettorale,
  WsRispostaPaginataListMembroListaElettorale,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * API per il recupero dell'immagine di una lista elettorale
 */
export const getBackListeElettoraliIdImmagine = (
  idListaElettorale: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Blob>(
    {
      url: `/framework-bo-procedimenti/liste-elettorali/${idListaElettorale}/immagine`,
      method: 'get',
      responseType: 'blob',
      signal,
    },
    options,
  );
};

export const getGetBackListeElettoraliIdImmagineQueryKey = (
  idListaElettorale: string,
) =>
  [
    `/framework-bo-procedimenti/liste-elettorali/${idListaElettorale}/immagine`,
  ] as const;

export const getGetBackListeElettoraliIdImmagineQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackListeElettoraliIdImmagine>>,
  TError = ErrorType<unknown>,
>(
  idListaElettorale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackListeElettoraliIdImmagine>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackListeElettoraliIdImmagine>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackListeElettoraliIdImmagineQueryKey(idListaElettorale);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackListeElettoraliIdImmagine>>
  > = ({ signal }) =>
    getBackListeElettoraliIdImmagine(idListaElettorale, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idListaElettorale, ...queryOptions };
};

export type GetBackListeElettoraliIdImmagineQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackListeElettoraliIdImmagine>>
>;
export type GetBackListeElettoraliIdImmagineQueryError = ErrorType<unknown>;

export const useGetBackListeElettoraliIdImmagine = <
  TData = Awaited<ReturnType<typeof getBackListeElettoraliIdImmagine>>,
  TError = ErrorType<unknown>,
>(
  idListaElettorale: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackListeElettoraliIdImmagine>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackListeElettoraliIdImmagineQueryOptions(
    idListaElettorale,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * API per il salvataggio/aggiornamento dell'immagine di una lista elettorale. Sovrascrive l'eventuale immagine preesistente. Formati accettati: image/jpeg, image/png, image/webp
 */
export const putBackListeElettoraliIdImmagine = (
  idListaElettorale: string,
  putBackListeElettoraliIdImmagineBody: PutBackListeElettoraliIdImmagineBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append('file', putBackListeElettoraliIdImmagineBody.file);

  return apiClientRequest<ListaElettorale>(
    {
      url: `/framework-bo-procedimenti/liste-elettorali/${idListaElettorale}/immagine`,
      method: 'put',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getPutBackListeElettoraliIdImmagineMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackListeElettoraliIdImmagine>>,
    TError,
    { idListaElettorale: string; data: PutBackListeElettoraliIdImmagineBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackListeElettoraliIdImmagine>>,
  TError,
  { idListaElettorale: string; data: PutBackListeElettoraliIdImmagineBody },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackListeElettoraliIdImmagine>>,
    { idListaElettorale: string; data: PutBackListeElettoraliIdImmagineBody }
  > = props => {
    const { idListaElettorale, data } = props ?? {};

    return putBackListeElettoraliIdImmagine(
      idListaElettorale,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackListeElettoraliIdImmagineMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackListeElettoraliIdImmagine>>
>;
export type PutBackListeElettoraliIdImmagineMutationBody =
  PutBackListeElettoraliIdImmagineBody;
export type PutBackListeElettoraliIdImmagineMutationError = ErrorType<unknown>;

export const usePutBackListeElettoraliIdImmagine = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackListeElettoraliIdImmagine>>,
    TError,
    { idListaElettorale: string; data: PutBackListeElettoraliIdImmagineBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutBackListeElettoraliIdImmagineMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API per la cancellazione dell'immagine di una lista elettorale
 */
export const deleteBackListeElettoraliIdImmagine = (
  idListaElettorale: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ListaElettorale>(
    {
      url: `/framework-bo-procedimenti/liste-elettorali/${idListaElettorale}/immagine`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteBackListeElettoraliIdImmagineMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackListeElettoraliIdImmagine>>,
    TError,
    { idListaElettorale: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteBackListeElettoraliIdImmagine>>,
  TError,
  { idListaElettorale: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteBackListeElettoraliIdImmagine>>,
    { idListaElettorale: string }
  > = props => {
    const { idListaElettorale } = props ?? {};

    return deleteBackListeElettoraliIdImmagine(
      idListaElettorale,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteBackListeElettoraliIdImmagineMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteBackListeElettoraliIdImmagine>>
>;

export type DeleteBackListeElettoraliIdImmagineMutationError =
  ErrorType<unknown>;

export const useDeleteBackListeElettoraliIdImmagine = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackListeElettoraliIdImmagine>>,
    TError,
    { idListaElettorale: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteBackListeElettoraliIdImmagineMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API per la ricerca del dettaglio di una lista elettorale
 */
export const getBackListeElettoraliId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ListaElettorale>(
    {
      url: `/framework-bo-procedimenti/liste-elettorali/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackListeElettoraliIdQueryKey = (id: string) =>
  [`/framework-bo-procedimenti/liste-elettorali/${id}`] as const;

export const getGetBackListeElettoraliIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackListeElettoraliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackListeElettoraliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackListeElettoraliId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackListeElettoraliIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackListeElettoraliId>>
  > = ({ signal }) => getBackListeElettoraliId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetBackListeElettoraliIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackListeElettoraliId>>
>;
export type GetBackListeElettoraliIdQueryError = ErrorType<unknown>;

export const useGetBackListeElettoraliId = <
  TData = Awaited<ReturnType<typeof getBackListeElettoraliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackListeElettoraliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackListeElettoraliIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * API per aggiornare una lista elettorale
 */
export const putBackListeElettoraliId = (
  id: string,
  listaElettoraleRequest: ListaElettoraleRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ListaElettorale>(
    {
      url: `/framework-bo-procedimenti/liste-elettorali/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: listaElettoraleRequest,
    },
    options,
  );
};

export const getPutBackListeElettoraliIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackListeElettoraliId>>,
    TError,
    { id: string; data: ListaElettoraleRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackListeElettoraliId>>,
  TError,
  { id: string; data: ListaElettoraleRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackListeElettoraliId>>,
    { id: string; data: ListaElettoraleRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putBackListeElettoraliId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackListeElettoraliIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackListeElettoraliId>>
>;
export type PutBackListeElettoraliIdMutationBody = ListaElettoraleRequest;
export type PutBackListeElettoraliIdMutationError = ErrorType<unknown>;

export const usePutBackListeElettoraliId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackListeElettoraliId>>,
    TError,
    { id: string; data: ListaElettoraleRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutBackListeElettoraliIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API per eliminare una lista elettorale
 */
export const deleteBackListeElettoraliId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ListaElettorale>(
    {
      url: `/framework-bo-procedimenti/liste-elettorali/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteBackListeElettoraliIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackListeElettoraliId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteBackListeElettoraliId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteBackListeElettoraliId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteBackListeElettoraliId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteBackListeElettoraliIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteBackListeElettoraliId>>
>;

export type DeleteBackListeElettoraliIdMutationError = ErrorType<unknown>;

export const useDeleteBackListeElettoraliId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackListeElettoraliId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteBackListeElettoraliIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API per la ricerca paginata delle liste elettorali, con la possibilità di filtrare i dati
 */
export const getBackListeElettorali = (
  params?: GetBackListeElettoraliParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListListaElettorale>(
    {
      url: `/framework-bo-procedimenti/liste-elettorali`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackListeElettoraliQueryKey = (
  params?: GetBackListeElettoraliParams,
) =>
  [
    `/framework-bo-procedimenti/liste-elettorali`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackListeElettoraliQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackListeElettorali>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackListeElettoraliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackListeElettorali>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackListeElettorali>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackListeElettoraliQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackListeElettorali>>
  > = ({ signal }) => getBackListeElettorali(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackListeElettoraliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackListeElettorali>>
>;
export type GetBackListeElettoraliQueryError = ErrorType<unknown>;

export const useGetBackListeElettorali = <
  TData = Awaited<ReturnType<typeof getBackListeElettorali>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackListeElettoraliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackListeElettorali>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackListeElettoraliQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * API per creare una nuova lista elettorale
 */
export const postBackListeElettorali = (
  listaElettoraleRequest: ListaElettoraleRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ListaElettorale>(
    {
      url: `/framework-bo-procedimenti/liste-elettorali`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: listaElettoraleRequest,
    },
    options,
  );
};

export const getPostBackListeElettoraliMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackListeElettorali>>,
    TError,
    { data: ListaElettoraleRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackListeElettorali>>,
  TError,
  { data: ListaElettoraleRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackListeElettorali>>,
    { data: ListaElettoraleRequest }
  > = props => {
    const { data } = props ?? {};

    return postBackListeElettorali(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackListeElettoraliMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackListeElettorali>>
>;
export type PostBackListeElettoraliMutationBody = ListaElettoraleRequest;
export type PostBackListeElettoraliMutationError = ErrorType<unknown>;

export const usePostBackListeElettorali = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackListeElettorali>>,
    TError,
    { data: ListaElettoraleRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostBackListeElettoraliMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API per l'importazione dei membri di una lista, tramite un file in formato csv oppure xlsx.
 */
export const postBackListeElettoraliIdImport = (
  id: string,
  postBackListeElettoraliIdImportBody: PostBackListeElettoraliIdImportBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append('file', postBackListeElettoraliIdImportBody.file);

  return apiClientRequest<BEResponseVoid>(
    {
      url: `/framework-bo-procedimenti/liste-elettorali/${id}/import`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getPostBackListeElettoraliIdImportMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackListeElettoraliIdImport>>,
    TError,
    { id: string; data: PostBackListeElettoraliIdImportBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackListeElettoraliIdImport>>,
  TError,
  { id: string; data: PostBackListeElettoraliIdImportBody },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackListeElettoraliIdImport>>,
    { id: string; data: PostBackListeElettoraliIdImportBody }
  > = props => {
    const { id, data } = props ?? {};

    return postBackListeElettoraliIdImport(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackListeElettoraliIdImportMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackListeElettoraliIdImport>>
>;
export type PostBackListeElettoraliIdImportMutationBody =
  PostBackListeElettoraliIdImportBody;
export type PostBackListeElettoraliIdImportMutationError = ErrorType<unknown>;

export const usePostBackListeElettoraliIdImport = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackListeElettoraliIdImport>>,
    TError,
    { id: string; data: PostBackListeElettoraliIdImportBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackListeElettoraliIdImportMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API per la ricerca paginata dei membri di una lista elettorale, con la possibilità di filtrare i dati
 */
export const getBackListeElettoraliIdMembri = (
  idListaElettorale: string,
  params?: GetBackListeElettoraliIdMembriParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListMembroListaElettorale>(
    {
      url: `/framework-bo-procedimenti/liste-elettorali/${idListaElettorale}/membri`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackListeElettoraliIdMembriQueryKey = (
  idListaElettorale: string,
  params?: GetBackListeElettoraliIdMembriParams,
) =>
  [
    `/framework-bo-procedimenti/liste-elettorali/${idListaElettorale}/membri`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackListeElettoraliIdMembriQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackListeElettoraliIdMembri>>,
  TError = ErrorType<unknown>,
>(
  idListaElettorale: string,
  params?: GetBackListeElettoraliIdMembriParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackListeElettoraliIdMembri>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackListeElettoraliIdMembri>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackListeElettoraliIdMembriQueryKey(idListaElettorale, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackListeElettoraliIdMembri>>
  > = ({ signal }) =>
    getBackListeElettoraliIdMembri(
      idListaElettorale,
      params,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!idListaElettorale, ...queryOptions };
};

export type GetBackListeElettoraliIdMembriQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackListeElettoraliIdMembri>>
>;
export type GetBackListeElettoraliIdMembriQueryError = ErrorType<unknown>;

export const useGetBackListeElettoraliIdMembri = <
  TData = Awaited<ReturnType<typeof getBackListeElettoraliIdMembri>>,
  TError = ErrorType<unknown>,
>(
  idListaElettorale: string,
  params?: GetBackListeElettoraliIdMembriParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackListeElettoraliIdMembri>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackListeElettoraliIdMembriQueryOptions(
    idListaElettorale,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
